/**
 * ImpersonationBanner Component
 *
 * Displays a persistent warning banner for active impersonation sessions.
 * Supports both admin impersonation and service bureau login-as sessions.
 */
import React, { useEffect, useState } from "react";
import { Box, Text, Button, Group, Stack, Paper } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { AppRoutes, backend_url } from "@/constants";
import { setLoggingOut } from "@/interceptors/axiosIntercepter";
import "./ImpersonationBanner.scss";

interface AdminImpersonationInfo {
  kind: "admin";
  admin_email: string;
  taxpro_email: string;
  is_impersonation: boolean;
}

interface ServiceBureauImpersonationInfo {
  kind: "service_bureau";
  bureau_email: string;
  target_email: string;
  is_service_bureau_impersonation: boolean;
}

type ImpersonationInfo =
  AdminImpersonationInfo | ServiceBureauImpersonationInfo;

/**
 * Compile-time exhaustiveness guard for discriminated unions. If a new `kind` is added to
 * `ImpersonationInfo` but not handled in the caller's switch, TypeScript fails to assign to `never`.
 */
function assertUnreachableBannerKind(_value: never): React.ReactNode {
  return (
    <Text size="xs" c="violet.9">
      Active impersonation session
    </Text>
  );
}

function impersonationBannerBody(info: ImpersonationInfo): React.ReactNode {
  switch (info.kind) {
    case "admin":
      return (
        <Group gap="xs">
          <strong>Admin session:</strong> {info.taxpro_email} (by{" "}
          {info.admin_email})
        </Group>
      );
    case "service_bureau":
      return (
        <Stack px="md" gap={0}>
          <Text fw={800} c="violet.9" component="span" size="xs">
            Login-as session:{" "}
          </Text>
          <Text c="violet.9" component="span" size="xs">
            {info.target_email}
          </Text>
        </Stack>
      );
    default:
      return assertUnreachableBannerKind(info);
  }
}

function revokePathForImpersonation(info: ImpersonationInfo): string {
  switch (info.kind) {
    case "service_bureau":
      return "/api/service-bureau/impersonation/revoke/";
    case "admin":
      return "/api/ext/admin-impersonation/revoke/";
    default: {
      const _exhaustive: never = info;
      throw new Error(
        `Unhandled impersonation kind: ${JSON.stringify(_exhaustive)}`
      );
    }
  }
}

/**
 * Reads impersonation info from cookie
 */
function getImpersonationInfo(): ImpersonationInfo | null {
  try {
    const cookies = document.cookie.split(";");
    const serviceBureauCookie = cookies.find((cookie) =>
      cookie.trim().startsWith("service_bureau_impersonation_info=")
    );
    const adminCookie = cookies.find((cookie) =>
      cookie.trim().startsWith("admin_impersonation_info=")
    );

    const parseCookieValue = (
      rawCookie: string
    ): Record<string, unknown> | null => {
      const cookieParts = rawCookie.trim().split("=");
      if (cookieParts.length < 2) {
        return null;
      }
      const cookieValue = cookieParts.slice(1).join("=").trim();
      if (!cookieValue) {
        return null;
      }
      let decoded: string;
      try {
        decoded = decodeURIComponent(cookieValue);
      } catch {
        decoded = cookieValue;
      }
      return JSON.parse(decoded) as Record<string, unknown>;
    };

    if (serviceBureauCookie) {
      const raw = parseCookieValue(serviceBureauCookie);
      if (
        raw?.is_service_bureau_impersonation === true &&
        typeof raw.bureau_email === "string" &&
        typeof raw.target_email === "string"
      ) {
        return {
          kind: "service_bureau",
          is_service_bureau_impersonation: true,
          bureau_email: raw.bureau_email,
          target_email: raw.target_email,
        };
      }
    }

    if (adminCookie) {
      const raw = parseCookieValue(adminCookie);
      if (
        raw?.is_impersonation === true &&
        typeof raw.admin_email === "string" &&
        typeof raw.taxpro_email === "string"
      ) {
        return {
          kind: "admin",
          is_impersonation: true,
          admin_email: raw.admin_email,
          taxpro_email: raw.taxpro_email,
        };
      }
    }

    return null;
  } catch (error) {
    // Silently fail - banner just won't show if cookie is invalid
    return null;
  }
}

const ImpersonationBanner: React.FC = () => {
  const [impersonationInfo, setImpersonationInfo] =
    useState<ImpersonationInfo | null>(null);

  useEffect(() => {
    // Check for impersonation info on mount and periodically
    const checkImpersonation = () => {
      const info = getImpersonationInfo();
      setImpersonationInfo(info);
    };

    // Check immediately
    checkImpersonation();

    // Also check after a short delay to handle cookie setting after redirect
    const initialDelay = setTimeout(checkImpersonation, 500);

    // Check every 5 seconds in case cookie is updated (consistent with extension)
    const interval = setInterval(checkImpersonation, 5000);

    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, []);

  if (!impersonationInfo) {
    return null;
  }

  const handleEndImpersonation = async () => {
    // Set flag to prevent axios interceptor from refreshing tokens
    setLoggingOut(true);

    // Drop the impersonated pro's cached state before handing the browser back.
    // Revoke only clears the cookie, so without this their `businessInfo` /
    // `personalInfo` stay in the admin's own storage and seed screens rendered
    // under the admin's session. Done up front because both branches below
    // navigate away, and auth here is cookie-based so nothing needs storage.
    sessionStorage.clear();
    localStorage.clear();

    const revokePath = revokePathForImpersonation(impersonationInfo);
    try {
      const response = await fetch(`${backend_url}${revokePath}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to end impersonation (${response.status})`);
      }

      // Full navigation so the browser commits revoke Set-Cookie before any SPA
      // token/check runs. Otherwise a stale impersonation access cookie can win and
      // token/check would rotate cookies for the impersonated user.
      window.location.assign(AppRoutes.root);
    } catch (error) {
      console.error("Error revoking impersonation session:", error);
      window.location.replace(AppRoutes.login);
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <Box className="impersonation-banner-container">
      <Paper
        className="impersonation-banner"
        color="violet.1"
        bg="violet.1"
        radius="md"
      >
        <Group
          justify="space-between"
          align="center"
          wrap="nowrap"
          gap="xs"
          style={{ height: "100%" }}
        >
          <Box style={{ lineHeight: "24px", fontSize: "11px" }}>
            {impersonationBannerBody(impersonationInfo)}
          </Box>
          <Button
            size="sm"
            variant="white"
            color="purple"
            onClick={handleEndImpersonation}
            rightSection={<IconX size={10} />}
            style={{
              flexShrink: 0,
              height: "20px",
              fontSize: "10px",
              padding: "0 8px",
            }}
          >
            End
          </Button>
        </Group>
      </Paper>
    </Box>
  );
};

export default ImpersonationBanner;
